import React, { useState, useEffect, useRef } from 'react';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { Transform } from 'libs/end-to-end/src/types/databases/transform/transform';
import { TransformReferenceWithOutputConsoleUrl } from 'libs/end-to-end/src/types/databases/transform/transform';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import cn from 'classnames';
import { useTransformPage } from 'apps/sigma2-ui/src/data-wizard/lib/useTransformPage';
import { TransformListFetcher } from 'apps/sigma2-ui/src/data-wizard/lib/useTransformPage';
import { TransformDataSetSelector } from 'apps/sigma2-ui/src/data-wizard/components/TransformDataSetSelector';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { TransformEditorUI } from 'apps/sigma2-ui/src/data-wizard/components/transform-page/TransformEditorUI';
import { TransformComparisonDropdown } from 'apps/sigma2-ui/src/data-wizard/components/transform-page/TransformComparisonDropdown';
import TransformComparisonTable from './TransformComparisonTable';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';

export const TransformPage = () => {
  const {
    code,
    setCode,
    isRunningTransform,
    transformId,
    setTransformId,
    taggedDataSetReferences,
    setTaggedDataSetReferences,
    organizationId,
    databaseId,
    transforms,
    mostRecentErrorMessage,
    continueToSchemaButtonLabel,
    continueToSchemaButtonDisabled,
    isLoadingScript,
    continueToSchemaButtonBehavior,
    transformDetails,
    runTransformButtonDisabled,
    runTransformButtonBehavior,
    runTransformButtonLabel,
    usePresetCodeButtonDisabled,
    usePresetCodeButtonBehavior,
    usePresetCodeButtonLabel,
    viewVisualizationButtonBehavior,
    viewVisualizationButtonDisabled,
    viewVisualizationButtonLabel,
    runOnBatch,
    setRunOnBatch,
    localTestCommand,
    transformType,
    setTransformType,
    isToBeCompared,
    setIsToBeCompared,
    platformTransformDetails,
    setPlatformTransformDetails,
    transformOptions,
    selectedTransformToCompare,
    setSelectedTransformToCompare,
    compareOutputContents,
    tryReloadCompare,
    tryReloadDataSummary,
    dataSummary,
    entitledDagsterTransforms,
    selectedDagsterTransformId,
    setSelectedDagsterTransformId,
    currentPage,
    setCurrentPage,
    totalCount,
    pageSize,
    totalPages,
    isTransformsLoading,
    setTransformsListState,
    onRegisterReloadTransforms,
  } = useTransformPage();

  return (
    <AdminLayout currentPage={'Transform'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Organizations', to: '/organizations' },
          {
            label: 'Databases',
            to: `/organizations/${organizationId}/databases`,
          },
          {
            label: 'Transforms',
            to: `/organizations/${organizationId}/databases/${databaseId}/transform`,
          },
        ]}
      />
      <div className="flex h-full w-full">
        <div className="w-3/4">
          <Spacing size={'md'} direction={'vertical'}>
            {transformType !== 'dagster' && (
              <TransformDataSetSelector
                setTaggedDataSetReferences={setTaggedDataSetReferences}
                taggedDataSetReferences={taggedDataSetReferences}
                organizationId={organizationId}
                databaseId={databaseId}
                isRunningTransform={isRunningTransform}
              />
            )}

            {mostRecentErrorMessage && (
              <S2Typography
                variant={'body-7'}
                textColorVariant={'red-800'}
                translatedContent={mostRecentErrorMessage}
              />
            )}

            {localTestCommand.trim() !== '' && (
              <div>
                <S2Typography
                  variant={'body-7'}
                  textColorVariant={'royal-900'}
                  translatedContent={'Local test command:'}
                />
                <textarea
                  className="form-textarea w-full h-32"
                  value={localTestCommand}
                  readOnly
                ></textarea>
              </div>
            )}

            <TransformEditorUI
              code={code}
              setCode={setCode}
              isRunningExecution={isRunningTransform}
              isLoadingScript={isLoadingScript}
              runOnBatch={runOnBatch}
              setRunOnBatch={setRunOnBatch}
              isToBeCompared={isToBeCompared}
              setIsToBeCompared={setIsToBeCompared}
              transformType={transformType}
              setTransformType={setTransformType}
              platformTransformDetails={platformTransformDetails}
              setPlatformTransformDetails={setPlatformTransformDetails}
              entitledDagsterTransforms={entitledDagsterTransforms}
              selectedDagsterTransformId={selectedDagsterTransformId}
              setSelectedDagsterTransformId={setSelectedDagsterTransformId}
            />

            {isToBeCompared && (
              <TransformComparisonDropdown
                options={transformOptions}
                onSelect={setSelectedTransformToCompare}
                value={selectedTransformToCompare}
              />
            )}

            <div className="flex h-full w-full flex-col space-y-4">
              {/* Buttons Section */}
              <div className="flex space-x-4">
                <Button
                  behavior={runTransformButtonBehavior}
                  disabled={runTransformButtonDisabled}
                >
                  {runTransformButtonLabel}
                </Button>

                <Button
                  behavior={usePresetCodeButtonBehavior}
                  disabled={usePresetCodeButtonDisabled}
                >
                  {usePresetCodeButtonLabel}
                </Button>

                <Button
                  behavior={viewVisualizationButtonBehavior}
                  disabled={viewVisualizationButtonDisabled}
                >
                  {viewVisualizationButtonLabel}
                </Button>

                <Button
                  behavior={continueToSchemaButtonBehavior}
                  disabled={continueToSchemaButtonDisabled}
                >
                  {continueToSchemaButtonLabel}
                </Button>
              </div>

              {/* Details Section */}
              <div className="flex-1">
                {transformDetails && (
                  <TransformDetails
                    transformReference={transformDetails.reference}
                    transform={transformDetails.transform}
                    rawCompareOutput={compareOutputContents ?? ''}
                    dataSummary={dataSummary}
                  />
                )}
              </div>
            </div>
          </Spacing>
        </div>
        <div className="w-1/4">
          <TransformListFetcher
            key={currentPage}
            organizationId={organizationId}
            databaseId={databaseId}
            page={currentPage}
            onStateChange={setTransformsListState}
            onRegisterReloadTransforms={onRegisterReloadTransforms}
          />
          {(isTransformsLoading || totalCount > 0) && (
            <TransformListPagination
              currentPage={currentPage}
              totalPages={
                isTransformsLoading ? Math.max(1, currentPage) : totalPages
              }
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              isLoading={isTransformsLoading}
            />
          )}
          <TransformsList
            transforms={transforms}
            transformId={transformId}
            setTransformId={setTransformId}
            tryReloadCompare={tryReloadCompare}
            tryReloadDataSummary={tryReloadDataSummary}
          />
        </div>
      </div>
    </AdminLayout>
  );
};

const TransformListPagination = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  isLoading = false,
}: {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}) => {
  const [jumpPage, setJumpPage] = useState(String(currentPage));

  useEffect(() => {
    setJumpPage(String(currentPage));
  }, [currentPage]);

  const handleJump = () => {
    const parsed = parseInt(jumpPage, 10);
    if (!Number.isNaN(parsed) && parsed >= 1 && parsed <= totalPages) {
      onPageChange(parsed);
    }
  };

  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="flex flex-wrap items-center gap-3 p-3 border-b mb-2">
      <button
        type="button"
        onClick={() => {
          onPageChange(currentPage - 1);
        }}
        disabled={isLoading || currentPage <= 1}
        className="p-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Previous page"
      >
        ← Prev
      </button>
      <button
        type="button"
        onClick={() => {
          onPageChange(currentPage + 1);
        }}
        disabled={isLoading || currentPage >= totalPages}
        className="p-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Next page"
      >
        Next →
      </button>
      <span className="text-sm text-gray-600">
        Page {currentPage} of {totalPages}
        {isLoading && ' (loading…)'}
        {!isLoading && totalCount > 0 && (
          <>
            {' '}
            ({start}–{end} of {totalCount})
          </>
        )}
      </span>
      <div className="flex items-center gap-1">
        <label htmlFor="transform-page-jump" className="text-sm">
          Jump to:
        </label>
        <input
          id="transform-page-jump"
          type="number"
          min={1}
          max={totalPages}
          value={jumpPage}
          onChange={(e) => {
            setJumpPage(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleJump();
            }
          }}
          className="w-14 border rounded px-1 py-0.5 text-sm form-input"
        />
        <button
          type="button"
          onClick={() => {
            handleJump();
          }}
          disabled={isLoading}
          className="p-1 rounded border text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Go
        </button>
      </div>
    </div>
  );
};

const TransformsList = ({
  transformId,
  transforms,
  setTransformId,
  tryReloadCompare,
  tryReloadDataSummary,
}: {
  transforms: TransformReferenceWithOutputConsoleUrl[];
  transformId: string | null;
  setTransformId: Setter<string | null>;
  tryReloadCompare: () => void;
  tryReloadDataSummary: () => void;
}) => {
  return (
    <div>
      {transforms.map((transform) => {
        const date = new Date(transform.startedAt);
        return (
          <div className="pt-0.5 pb-0.5 px-0.5" key={transform.id}>
            <Card
              key={transform.id}
              paddingClassName="p-3"
              className={cn({
                'border-2 border-blue-900': transformId === transform.id,
              })}
              onClick={() => {
                setTransformId(transform.id);
                tryReloadCompare();
                tryReloadDataSummary();
              }}
            >
              <div key={transform.id}>
                <div className="flex items-center gap-1">
                  <div
                    className={cn('text-xs font-bold', {
                      'text-green-600': transform.status === 'Succeeded',
                      'text-red-600': transform.status === 'Failed',
                      'text-blue-600': transform.status === 'Running',
                      'text-gray-600': transform.status === 'NotStarted',
                    })}
                  >
                    {transform.status}
                  </div>
                  <div className="text-[11px] text-gray-600">
                    {date.toLocaleDateString()}
                  </div>
                </div>
                <div className="text-[14px] text-gray-600">
                  {date.toLocaleTimeString()}
                </div>
                <div
                  className="text-[9px] text-gray-400 truncate"
                  title={transform.id}
                >
                  {transform.id}
                </div>
              </div>
            </Card>
          </div>
        );
      })}
    </div>
  );
};

const tryParseJson = (str: string): unknown | null => {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};

const isErrorRecord = (obj: unknown): obj is Record<string, unknown> =>
  typeof obj === 'object' && obj !== null;

const formatReasonsArray = (record: Record<string, unknown>): string | null => {
  if (!Array.isArray(record.reasons) || record.reasons.length === 0) {
    return null;
  }
  const prefix = record.errorType ? `${String(record.errorType)}: ` : '';
  return prefix + record.reasons.map((r) => String(r)).join('; ');
};

const getErrorTypeOnly = (record: Record<string, unknown>): string | null => {
  if (
    typeof record.errorType === 'string' &&
    !record.errorMessage &&
    !record.Cause
  ) {
    return record.errorType;
  }
  return null;
};

const extractErrorReason = (obj: unknown, depth = 0): string => {
  if (depth > 5 || !isErrorRecord(obj)) {
    return '';
  }

  // Check for errorReason string
  if (typeof obj.errorReason === 'string') {
    return obj.errorReason;
  }

  // Check for reasons array
  const reasonsResult = formatReasonsArray(obj);
  if (reasonsResult) {
    return reasonsResult;
  }

  // Check for errorType alone
  const errorTypeResult = getErrorTypeOnly(obj);
  if (errorTypeResult) {
    return errorTypeResult;
  }

  // Recurse into nested error structures
  const nextValue = obj.errorMessage ?? obj.Cause;
  if (typeof nextValue === 'string') {
    const parsed = tryParseJson(nextValue);
    return parsed ? extractErrorReason(parsed, depth + 1) : '';
  }

  return '';
};

/**
 * Recursively parse all nested JSON strings into objects for clean display
 */
const deepParseJson = (obj: unknown): unknown => {
  if (typeof obj === 'string') {
    const parsed = tryParseJson(obj);
    return parsed ? deepParseJson(parsed) : obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(deepParseJson);
  }

  if (typeof obj === 'object' && obj !== null) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = deepParseJson(value);
    }
    return result;
  }

  return obj;
};

const parseNestedError = (
  msg: string,
): { reason: string; rawPretty: string | null } => {
  const parsed = tryParseJson(msg);
  const reason = parsed ? extractErrorReason(parsed) : '';

  // Deep parse to convert all nested JSON strings to objects
  const fullyParsed = parsed ? deepParseJson(parsed) : null;
  const rawPretty = fullyParsed ? JSON.stringify(fullyParsed, null, 2) : null;

  return { reason, rawPretty };
};

const FormattedError = ({ errorMessage }: { errorMessage: string }) => {
  const [showRaw, setShowRaw] = useState(true); // Expanded by default
  const [showToast, setShowToast] = useState(true);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const { reason, rawPretty } = parseNestedError(errorMessage);
  const errorRef = useRef<HTMLDivElement>(null);

  // Check if error box is visible in viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsErrorVisible(entry.isIntersecting);
      },
      { threshold: 0.1 },
    );

    if (errorRef.current) {
      observer.observe(errorRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Auto-hide toast after 8 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowToast(false);
    }, 8000);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  const scrollToError = () => {
    if (errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setShowToast(false);
  };

  return (
    <>
      {/* Floating Toast Notification */}
      {showToast && !isErrorVisible && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-fade-in">
          <div className="flex items-center gap-3 px-4 py-3 bg-red-600 text-white rounded-lg shadow-lg">
            {/* Error Icon */}
            <div className="flex-shrink-0 flex h-6 w-6 items-center justify-center rounded-full bg-white/20">
              <span className="text-sm font-bold">!</span>
            </div>
            <span className="text-sm font-medium max-w-xs truncate">
              {reason || 'Transform failed'}
            </span>

            {/* Jump Button */}
            <button
              onClick={scrollToError}
              className="flex items-center gap-1 px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-semibold transition-colors"
            >
              View
              <svg
                className="w-3 h-3 animate-bounce"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            </button>

            {/* Dismiss Button */}
            <button
              onClick={() => {
                setShowToast(false);
              }}
              className="flex-shrink-0 text-white/70 hover:text-white transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div ref={errorRef} className="space-y-3 scroll-mt-4">
        {/* Main Error Card */}
        <div className="relative overflow-hidden rounded-lg border border-red-200 bg-white shadow-sm">
          <div className="absolute left-0 top-0 h-full w-1 bg-red-500" />

          <div className="p-4 pl-5">
            {/* Error Header */}
            <div className="flex items-center gap-2 mb-2">
              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100 ring-1 ring-red-200">
                <span className="text-red-600 text-xs font-bold">!</span>
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-red-600">
                Transform Error
              </span>
            </div>

            {reason ? (
              <p className="text-sm font-medium text-red-700 leading-relaxed">
                {reason}
              </p>
            ) : (
              <p className="text-sm text-red-600 break-words font-mono">
                {errorMessage}
              </p>
            )}
          </div>
        </div>

        {reason && (
          <button
            className="group flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            onClick={() => {
              setShowRaw(!showRaw);
            }}
          >
            <span
              className={`inline-block transition-transform duration-200 text-red-400 ${showRaw ? 'rotate-90' : ''}`}
            >
              ▶
            </span>
            <span className="font-medium">
              {showRaw ? 'Hide' : 'View'} error details
            </span>
          </button>
        )}

        {showRaw && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 border-b border-gray-200">
              <svg
                className="w-4 h-4 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                />
              </svg>
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Stack Trace
              </span>
            </div>

            <pre className="p-4 text-xs font-mono text-gray-700 overflow-auto max-h-[500px] whitespace-pre-wrap leading-relaxed">
              {rawPretty ?? errorMessage}
            </pre>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translate(-50%, -20px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
};

const TransformDetails = ({
  transformReference,
  transform,
  rawCompareOutput,
  dataSummary,
}: {
  transformReference: TransformReferenceWithOutputConsoleUrl;
  transform: Transform;
  rawCompareOutput: string;
  dataSummary: string | null;
}) => {
  const [activeTab, setActiveTab] = useState<string>('details');

  const tabs = [
    { id: 'details', label: 'Details' },
    ...(transform.transformIdToCompare
      ? [{ id: 'compare', label: 'Comparison' }]
      : []),
    { id: 'data-summary', label: 'Data Summary' },
  ];

  if (transform.details.status === 'Succeeded') {
    return (
      <div>
        {/* Tab Buttons */}
        <div className="flex border-b mb-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`px-4 py-2 ${
                activeTab === tab.id
                  ? 'border-b-2 border-blue-500 font-bold'
                  : 'text-gray-500'
              }`}
              onClick={() => {
                setActiveTab(tab.id);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '8px',
            border: '1px solid #ccc',
            borderRadius: '4px',
          }}
        >
          {activeTab === 'details' && (
            <div>
              <div className={'font-bold text-green-400'}>
                Execution succeeded
              </div>
              <div>
                <a
                  href={transformReference.outputConsoleUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-500 underline"
                >
                  View output
                </a>
              </div>
              {/* Add more content for the "Details" tab here */}
              <div>
                <p className="font-bold">Missing desired columns:</p>
                <p>
                  {transform.details.insights.desiredMissingColumns.join(
                    ', ',
                  ) || 'N/A'}
                </p>
              </div>
              <div>
                <p className="font-bold">Missing optional columns:</p>
                <p>
                  {transform.details.insights.optionalMissingColumns.join(
                    ', ',
                  ) || 'N/A'}
                </p>
              </div>

              <div>
                <p className="font-bold">Visible desired columns:</p>
                <p>
                  {transform.details.insights.desiredVisibleColumns.join(
                    ', ',
                  ) || 'N/A'}
                </p>
              </div>
              <div>
                <p className="font-bold">Visible optional columns:</p>
                <p>
                  {transform.details.insights.optionalVisibleColumns.join(
                    ', ',
                  ) || 'N/A'}
                </p>
              </div>
              <div>
                <p className="font-bold">Visible custom columns:</p>
                <p>
                  {transform.details.insights.customVisibleColumns.join(', ') ||
                    'N/A'}
                </p>
              </div>
              <div>
                <p className="font-bold">Possible custom dimension columns:</p>
                {transform.details.insights.possibleDimensionColumns.map(
                  (stuff) => {
                    return (
                      <p key={stuff.name}>
                        <span className="font-semibold">{stuff.name}:</span>{' '}
                        {stuff.values.join(', ')}
                      </p>
                    );
                  },
                )}
              </div>

              <div>
                <p className="font-bold">Possible custom metric columns:</p>
                {transform.details.insights.possibleMetricColumns.join(', ')}
              </div>

              <div>
                <S2Typography
                  variant={'body-1'}
                  textColorVariant={'gray-900'}
                  translatedContent={'Department / Job Title Maps:'}
                />
                <Spacing size={'md'} direction={'vertical'}>
                  {Object.entries(
                    transform.details.insights
                      .departmentToJobTitleColumnValues ?? {},
                  ).map(([department, jobTitles]) => {
                    return (
                      <Spacing
                        size={'xs'}
                        direction={'vertical'}
                        key={department}
                      >
                        <S2Typography
                          variant={'body-1'}
                          textColorVariant={'gray-900'}
                          translatedContent={department}
                        />
                        {jobTitles.sort().map((jobTitle) => {
                          return (
                            <S2Typography
                              variant={'body-1'}
                              textColorVariant={'gray-900'}
                              translatedContent={jobTitle}
                              key={jobTitle}
                            />
                          );
                        })}
                      </Spacing>
                    );
                  })}
                </Spacing>
              </div>
            </div>
          )}

          {activeTab === 'compare' && (
            <div>
              <TransformComparisonTable rawOutput={rawCompareOutput} />
            </div>
          )}

          {activeTab === 'data-summary' && (
            <div>
              <div className={'font-bold text-green-400'}>Data summary</div>
              <div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: dataSummary?.replaceAll('\n', '<br />') ?? 'N/A',
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (transform.details.status === 'Failed') {
    return (
      <div>
        <div className={'font-bold mb-2'}>Execution failed</div>
        <FormattedError errorMessage={transform.details.errorMessage} />
      </div>
    );
  }

  if (
    transform.details.status === 'Running' ||
    transform.details.status === 'NotStarted'
  ) {
    console.log('Up to Running or NotStarted');
    return <div>Analyzing</div>;
  }

  throw guardExhaustive(transform.details);
};
